// shared-lib/vanity-assembly/counterOverhang.js
//
// How far a vanity's counter stands in front of its carcass, and therefore how
// deep the counter is.
//
// THE SINK CAN EXTEND BEYOND THE CABINET (user ruling, 2026-09-25). A bowl
// hangs from the COUNTER, and a counter may overhang its cabinet — which is
// what makes a console basin on a shallow carcass a real form rather than an
// impossible one. Two places in this subsystem had quietly assumed otherwise:
// the scan path grows a shallow box toward `SHALLOW_VANITY_DEPTH` as though
// the CARCASS had to reach the fit depth, and `buildVanitySinkBasin` clamps
// its bowl to `cabinetDepth` (its own comment flags that as an undermount
// simplification, "not a universal rule").
//
// The fit rule is unchanged and still does the deriving:
//
//     counter  >= cutout + 2 x SINK_FIT_MARGINS.depth      (15.31")
//     overhang  = counter - carcass
//
// so an ordinary 21" vanity keeps the derived 1.25" and nothing about it
// moves; only a carcass too shallow to reach the fit depth spends more, up to
// `VANITY_BASIN_OVERHANG_MAX` (6", the one chosen number — see its docstring
// for what that buys and why the cap is a judgement).
//
// Pure. No I/O.

const {
  SHALLOW_VANITY_DEPTH,
  VANITY_BASIN_OVERHANG,
  VANITY_BASIN_OVERHANG_MAX,
} = require('./constants');

/**
 * The front overhang a carcass of `carcassDepth` should carry.
 *
 * The derived margin for anything deep enough to hold a bowl on its own; the
 * shortfall for anything shallower, capped. Never less than the derived
 * margin, so this can only ever push a counter FORWARD — it is not a way to
 * make a standard vanity's counter flush with its doors.
 *
 * @param {number} carcassDepth metres. Missing/degenerate → `minOverhang`.
 * @param {number} [minOverhang] the caller's own standard front overhang,
 *   defaulting to the fit rule's derived margin. A vanity RUN passes its
 *   profile's 1" so that every ordinary run is untouched and only a shallow
 *   carcass spends more — growing the standard case by a quarter inch would
 *   move counters on vanities that have no fit problem at all.
 * @returns {number} metres.
 */
function vanityCounterFrontOverhang(
  carcassDepth,
  minOverhang = VANITY_BASIN_OVERHANG
) {
  if (!Number.isFinite(carcassDepth) || carcassDepth <= 0) return minOverhang;
  const shortfall = SHALLOW_VANITY_DEPTH - carcassDepth;
  return Math.min(Math.max(minOverhang, shortfall), VANITY_BASIN_OVERHANG_MAX);
}

/** The counter depth that overhang produces, front face to back. */
function vanityCounterDepth(carcassDepth, minOverhang) {
  return (
    (carcassDepth || 0) + vanityCounterFrontOverhang(carcassDepth, minOverhang)
  );
}

/**
 * Is a carcass this shallow still able to carry a catalog bowl, given the
 * overhang budget? False is the wall-hung unit's answer, not a failure.
 */
function carcassCanCarryBasin(carcassDepth, minOverhang) {
  return (
    vanityCounterDepth(carcassDepth, minOverhang) >= SHALLOW_VANITY_DEPTH - 1e-9
  );
}

module.exports = {
  vanityCounterFrontOverhang,
  vanityCounterDepth,
  carcassCanCarryBasin,
};
